/**
 * Invoice Management Tab — Phase 2 UI for the invoice OCR module.
 *
 * Sections:
 *   1. Upload / Inbox — drag-drop bulk upload, queue runner, progress per file.
 *   2. Pending Review — invoices waiting for human reconciliation. Each opens a
 *      review dialog showing OCR'd line items + auto-match suggestions. From the
 *      dialog the user picks ONE of three reconcile actions:
 *        a) Match to existing Material Order (most common)
 *        b) Convert to back-dated Material Order (labeled `From Invoice`)
 *        c) Log as standalone project expense (no order created)
 *   3. Reconciled — searchable history, click to view PDF + linked order.
 *
 * Behind `invoice_ocr` feature flag. Mounted on Company Admin + Accounts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  UploadCloud, FileText, RefreshCw, Trash2, ExternalLink, Loader2,
  CheckCircle2, AlertTriangle, Link2, FilePlus, Receipt, X, Search,
  RotateCcw, Eye, ArrowRight, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { ProjectCombobox } from '@/components/dashboard/ProjectCombobox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n, ccy = 'CAD') => {
  if (n === null || n === undefined || n === '') return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return `${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;
};

const variantLabel = (v) => {
  if (!v) return '';
  return [v.brand, v.size_spec, v.unit].filter(Boolean).join(' · ') || 'Variant';
};

// Heuristic: grab the most obvious size spec out of a supplier description so
// the "+ New variant" popover can pre-fill it. Catches dimensions like
// 1.0"x24"x48", 1.5"x48"x100', 3"x300', 1.6 MIL, 3.0lb, etc.
const extractSpecHint = (desc) => {
  if (!desc) return '';
  const patterns = [
    /(\d+(?:\.\d+)?\s*(?:MIL|mil|lb|LB))\s+\d?["'\s]*x?[\d"']*/,
    /(\d+(?:\.\d+)?["']?\s*x\s*\d+(?:\.\d+)?["']?(?:\s*x\s*\d+(?:\.\d+)?["']?)?)/,
    /(\d+(?:\.\d+)?\s*["']?\s*x\s*\d+\s*["']?)/,
  ];
  for (const re of patterns) {
    const m = desc.match(re);
    if (m) return m[1].replace(/\s+/g, ' ').trim();
  }
  return '';
};

const StatusBadge = ({ status }) => {
  const cfg = {
    pending: { label: 'Parsing…', cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: Loader2, spin: true },
    parsing: { label: 'Parsing…', cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: Loader2, spin: true },
    parsed: { label: 'Ready', cls: 'bg-blue-100 text-blue-800 border-blue-300', Icon: CheckCircle2 },
    failed: { label: 'Parse failed', cls: 'bg-red-100 text-red-800 border-red-300', Icon: AlertTriangle },
    pending_review: { label: 'Pending review', cls: 'bg-blue-100 text-blue-800 border-blue-300', Icon: Eye },
    reconciled_matched: { label: 'Matched to order', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: Link2 },
    reconciled_backdated: { label: 'Back-dated order', cls: 'bg-purple-100 text-purple-800 border-purple-300', Icon: FilePlus },
    logged_as_expense: { label: 'Logged as expense', cls: 'bg-cyan-100 text-cyan-800 border-cyan-300', Icon: Receipt },
  };
  const c = cfg[status] || { label: status || '—', cls: 'bg-slate-100 text-slate-700 border-slate-300', Icon: FileText };
  const Icon = c.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${c.cls}`}>
      <Icon className={`w-3 h-3 ${c.spin ? 'animate-spin' : ''}`} />
      {c.label}
    </span>
  );
};

// --------------------- Inline "+ New variant" popover ----------------------
function AddVariantButton({ typeId, typeName, defaultUnit, hintFromDescription, onCreate, testId }) {
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState('');
  const [sizeSpec, setSizeSpec] = useState('');
  const [unit, setUnit] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setBrand('');
      setSizeSpec(hintFromDescription || '');
      setUnit(defaultUnit || '');
    }
  }, [open, hintFromDescription, defaultUnit]);

  const submit = async () => {
    if (!sizeSpec.trim() && !brand.trim()) {
      toast.error('Add at least a brand or a size');
      return;
    }
    setSaving(true);
    await onCreate({ type_id: typeId, brand: brand.trim() || null, size_spec: sizeSpec.trim() || null, unit: unit.trim() || null });
    setSaving(false);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={`Add a new ${typeName} variant`}
          className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded border border-slate-300 bg-white hover:bg-blue-50 hover:border-blue-400 text-blue-600"
          data-testid={testId}
        >
          <Plus className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        <div className="text-xs font-semibold mb-1">Add a {typeName} variant</div>
        <p className="text-[10px] text-slate-500 mb-2">Saved for the whole company — picked here automatically.</p>
        <div className="space-y-2">
          <div>
            <Label className="text-[10px]">Brand</Label>
            <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. FSK, 3M" className="h-7 text-xs" data-testid={`${testId}-brand`} />
          </div>
          <div>
            <Label className="text-[10px]">Size / Spec</Label>
            <Input value={sizeSpec} onChange={(e) => setSizeSpec(e.target.value)} placeholder='e.g. 1.0"x24"x48", 1.6 MIL 3"' className="h-7 text-xs" data-testid={`${testId}-size`} />
          </div>
          <div>
            <Label className="text-[10px]">Unit</Label>
            <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="SF, RL, EA, BX…" className="h-7 text-xs" data-testid={`${testId}-unit`} />
          </div>
          <Button type="button" size="sm" className="w-full h-7 text-xs" onClick={submit} disabled={saving} data-testid={`${testId}-save`}>
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
            Save & select
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// --------------------- Inline "+ Create new project" popover ----------------
function NewProjectButton({ defaultAddress, defaultClientId, onCreated, testId }) {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');
  const [address, setAddress] = useState('');
  const [startDate, setStartDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setClientId(defaultClientId || '');
    setAddress(defaultAddress || '');
    const d = new Date();
    setStartDate(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/clients`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then((data) => setClients(Array.isArray(data) ? data : []))
      .catch(() => setClients([]));
  }, [open, defaultAddress, defaultClientId]);

  const submit = async () => {
    if (!name.trim() || !address.trim() || !clientId) {
      toast.error('Name, client and address are required');
      return;
    }
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          address: address.trim(),
          client_id: clientId,
          start_date: startDate || null,
          status: 'active',
          stage: 'construction',
        })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || 'Could not create project');
        return;
      }
      const created = await res.json();
      toast.success(`Project "${created.name}" created`);
      if (onCreated) await onCreated(created.id);
      setOpen(false);
    } catch {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-[11px] text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"
          data-testid={testId}
        >
          <Plus className="w-3 h-3" /> Create new project here
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[360px] p-3">
        <div className="text-sm font-semibold mb-1">Quick-create project</div>
        <p className="text-[11px] text-slate-500 mb-2">
          Different client at the same address? Spin up a new project without leaving this invoice.
        </p>
        <div className="space-y-2">
          <div>
            <Label className="text-[10px]">Project name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Yorkwood Condos — Client B" className="h-8 text-xs" data-testid={`${testId}-name`} />
          </div>
          <div>
            <Label className="text-[10px]">Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="h-8 text-xs" data-testid={`${testId}-client`}>
                <SelectValue placeholder="Pick a client…" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>
                ))}
                {clients.length === 0 && <SelectItem value="__none" disabled>No clients found</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Address *</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} className="h-8 text-xs" data-testid={`${testId}-address`} />
          </div>
          <div>
            <Label className="text-[10px]">Start date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-8 text-xs" data-testid={`${testId}-start`} />
          </div>
          <Button type="button" size="sm" className="w-full h-8 text-xs" onClick={submit} disabled={saving} data-testid={`${testId}-save`}>
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
            Create & select
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// ----------------------------- Reconcile Dialog -----------------------------
function ReviewDialog({ open, onOpenChange, invoiceId, projects, onReconciled, onProjectsChanged }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // 'match' | 'convert' | 'expense' | 'unreconcile'
  const [data, setData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [variants, setVariants] = useState([]); // all variants for the company
  const [savingLine, setSavingLine] = useState(null);
  const [convProjectId, setConvProjectId] = useState('');
  const [convNotes, setConvNotes] = useState('');
  const [expProjectId, setExpProjectId] = useState('');
  const [expNotes, setExpNotes] = useState('');
  const [action, setAction] = useState(null); // 'match' | 'convert' | 'expense'
  const [pickedOrderId, setPickedOrderId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const load = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${API_URL}/api/invoices/${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/invoices/${invoiceId}/suggested-orders`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/invoices/material-types`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/invoices/material-variants`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (r1.ok) {
        const inv = await r1.json();
        setData(inv);
        setConvProjectId(inv.project_id || '');
        setExpProjectId(inv.project_id || '');
      }
      if (r2.ok) setSuggestions(await r2.json());
      if (r3.ok) setMaterialTypes(await r3.json());
      if (r4.ok) setVariants(await r4.json());
    } catch {
      toast.error('Could not load invoice');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  // Persist a line-item → material-type + optional variant mapping. Backend
  // also writes the (supplier, description) alias so the next invoice
  // auto-fills both fields.
  const mapLineItem = async (lineItemId, typeId, variantId = null) => {
    if (!invoiceId || !lineItemId) return;
    setSavingLine(lineItemId);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/line-items/${lineItemId}/map`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type_id: typeId || null, variant_id: variantId || null, save_alias: !!typeId })
      });
      if (!res.ok) {
        toast.error('Could not save mapping');
        return;
      }
      setData(prev => prev && ({
        ...prev,
        line_items: (prev.line_items || []).map(li =>
          li.id === lineItemId ? { ...li, type_id: typeId, variant_id: variantId, match_status: typeId ? 'confirmed' : 'needs_review' } : li
        )
      }));
      if (typeId) toast.success('Mapped — next invoice from this supplier auto-fills');
    } catch {
      toast.error('Connection error');
    } finally {
      setSavingLine(null);
    }
  };

  // Add a new variant on the fly. Returns the new variant id (or null).
  const addVariant = async ({ type_id, brand, size_spec, unit }) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/invoices/material-variants`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type_id, brand: brand || null, size_spec: size_spec || null, unit: unit || null })
      });
      if (!res.ok) { toast.error('Could not save variant'); return null; }
      const v = await res.json();
      setVariants(prev => [...prev, v]);
      return v.id;
    } catch {
      toast.error('Connection error');
      return null;
    }
  };

  useEffect(() => {
    if (open) {
      setAction(null);
      setPickedOrderId(null);
      setData(null);
      setSuggestions([]);
      load();
    } else {
      if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
    }
  }, [open, invoiceId]); // eslint-disable-line

  const openPdf = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/invoices/file/${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setPdfUrl(url);
    } catch {
      toast.error('Could not load invoice file');
    }
  };

  const submit = async () => {
    const token = localStorage.getItem('token');
    try {
      let res;
      if (action === 'match') {
        if (!pickedOrderId) { toast.error('Select an order to match'); return; }
        setBusy('match');
        res = await fetch(`${API_URL}/api/invoices/${invoiceId}/match-order`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ order_id: pickedOrderId })
        });
      } else if (action === 'convert') {
        if (!convProjectId) { toast.error('Pick a project'); return; }
        setBusy('convert');
        res = await fetch(`${API_URL}/api/invoices/${invoiceId}/convert-to-order`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: convProjectId, notes: convNotes })
        });
      } else if (action === 'expense') {
        if (!expProjectId) { toast.error('Pick a project'); return; }
        setBusy('expense');
        res = await fetch(`${API_URL}/api/invoices/${invoiceId}/log-as-expense`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: expProjectId, notes: expNotes })
        });
      }
      if (!res || !res.ok) {
        const j = await res?.json().catch(() => ({}));
        toast.error(j?.detail || 'Reconcile failed');
        return;
      }
      toast.success('Reconciled');
      if (onReconciled) onReconciled();
      onOpenChange(false);
    } catch {
      toast.error('Connection error');
    } finally {
      setBusy(null);
    }
  };

  const unreconcile = async () => {
    if (!confirm('Revert this invoice back to pending review? Any back-dated order it created will be deleted.')) return;
    const token = localStorage.getItem('token');
    setBusy('unreconcile');
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/unreconcile`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error('Could not unreconcile'); return; }
      toast.success('Reverted to pending');
      if (onReconciled) onReconciled();
      onOpenChange(false);
    } finally { setBusy(null); }
  };

  const isReconciled = data?.reconcile_status && data.reconcile_status !== 'pending_review';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[920px] max-h-[95vh] overflow-y-auto" data-testid="invoice-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <FileText className="w-5 h-5 text-blue-600" />
            Review Invoice
            {data && <StatusBadge status={data.reconcile_status || data.parse_status} />}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Confirm what the OCR extracted, then pick an action below.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div>
        ) : !data ? (
          <div className="p-6 text-center text-slate-500">Invoice not found.</div>
        ) : (
          <div className="space-y-4">
            {/* Header summary */}
            <Card>
              <CardContent className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Supplier</div><div className="font-medium">{data.supplier_name || '—'}</div></div>
                <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Invoice #</div><div className="font-medium">{data.invoice_number || '—'}</div></div>
                <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Date</div><div className="font-medium">{data.invoice_date || '—'}</div></div>
                <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Total</div><div className="font-medium text-base">{fmtMoney(data.total, data.currency || 'CAD')}</div></div>
                <div><div className="text-[10px] uppercase text-slate-500 font-semibold">PO #</div><div className="font-medium">{data.po_number || '—'}</div></div>
                <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Subtotal</div><div className="font-medium">{fmtMoney(data.subtotal, data.currency || 'CAD')}</div></div>
                <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Tax</div><div className="font-medium">{fmtMoney(data.tax_amount, data.currency || 'CAD')}</div></div>
                <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Freight</div><div className="font-medium">{fmtMoney(data.freight, data.currency || 'CAD')}</div></div>
                <div className="col-span-2 sm:col-span-4"><div className="text-[10px] uppercase text-slate-500 font-semibold">Ship-to</div><div className="text-xs">{data.ship_to || '—'}</div></div>
              </CardContent>
            </Card>

            {/* Line items */}
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1 flex items-center justify-between">
                <span>Line items ({data.line_items?.length || 0})</span>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={openPdf} data-testid="open-invoice-pdf">
                  <ExternalLink className="w-3 h-3 mr-1" /> Open original
                </Button>
              </div>
              <div className="border rounded-lg overflow-x-auto max-h-[320px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Description</th>
                      <th className="px-2 py-1.5 text-left">Material type</th>
                      <th className="px-2 py-1.5 text-right">Qty</th>
                      <th className="px-2 py-1.5 text-left">Unit</th>
                      <th className="px-2 py-1.5 text-right">Unit price</th>
                      <th className="px-2 py-1.5 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.line_items || []).map((li, idx) => {
                      const typeVariants = variants.filter(v => v.type_id === li.type_id);
                      return (
                      <tr key={li.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-2 py-1 align-top">{li.description}</td>
                        <td className="px-2 py-1 align-top">
                          <div className="flex flex-col gap-1">
                            <select
                              value={li.type_id || ''}
                              onChange={(e) => mapLineItem(li.id, e.target.value || null, null)}
                              disabled={isReconciled || savingLine === li.id}
                              className={`w-full min-w-[150px] max-w-[200px] text-xs border rounded px-1.5 py-0.5 bg-white ${
                                li.type_id ? 'border-emerald-300 text-emerald-800' : 'border-amber-300 text-amber-700'
                              }`}
                              data-testid={`line-map-${li.id}`}
                            >
                              <option value="">— pick type —</option>
                              {materialTypes.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                            {li.type_id && !isReconciled && (
                              <div className="flex items-center gap-1">
                                <select
                                  value={li.variant_id || ''}
                                  onChange={(e) => mapLineItem(li.id, li.type_id, e.target.value || null)}
                                  disabled={savingLine === li.id}
                                  className={`flex-1 min-w-[120px] max-w-[170px] text-[11px] border rounded px-1.5 py-0.5 bg-white ${
                                    li.variant_id ? 'border-blue-300 text-blue-800' : 'border-slate-300 text-slate-600'
                                  }`}
                                  data-testid={`line-variant-${li.id}`}
                                >
                                  <option value="">— variant (optional) —</option>
                                  {typeVariants.map(v => (
                                    <option key={v.id} value={v.id}>{variantLabel(v)}</option>
                                  ))}
                                </select>
                                <AddVariantButton
                                  typeId={li.type_id}
                                  typeName={materialTypes.find(t => t.id === li.type_id)?.name || ''}
                                  defaultUnit={materialTypes.find(t => t.id === li.type_id)?.default_unit || li.unit || ''}
                                  hintFromDescription={extractSpecHint(li.description)}
                                  onCreate={async (payload) => {
                                    const newId = await addVariant(payload);
                                    if (newId) await mapLineItem(li.id, li.type_id, newId);
                                  }}
                                  testId={`add-variant-${li.id}`}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-1 text-right align-top">{li.quantity ?? '—'}</td>
                        <td className="px-2 py-1 align-top">{li.unit ?? '—'}</td>
                        <td className="px-2 py-1 text-right align-top">{fmtMoney(li.unit_price, data.currency || 'CAD')}</td>
                        <td className="px-2 py-1 text-right font-medium align-top">{fmtMoney(li.line_total, data.currency || 'CAD')}</td>
                      </tr>
                      );
                    })}
                    {(!data.line_items || data.line_items.length === 0) && (
                      <tr><td colSpan="6" className="text-center py-4 text-slate-400">No line items parsed.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {(data.line_items || []).some(li => !li.type_id) && (
                <div className="text-[11px] text-amber-700 mt-1 flex items-start gap-1.5">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>Pick a <b>Material type</b> for each line. Mappings are remembered per supplier so the next invoice auto-fills.</span>
                </div>
              )}
            </div>

            {/* Already reconciled state */}
            {isReconciled && (
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-3 text-xs">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <StatusBadge status={data.reconcile_status} />
                      <div className="mt-1 text-slate-700">
                        Reconciled by <b>{data.reconciled_by_name || '—'}</b>
                        {data.reconciled_at && ` on ${new Date(data.reconciled_at).toLocaleString()}`}
                        {data.project_name && <> · Project: <b>{data.project_name}</b></>}
                      </div>
                      {data.matched_order && (
                        <div className="mt-1 text-slate-700">
                          Linked order: <b>{data.matched_order.id}</b>
                          {data.matched_order.source === 'invoice_backdated' && (
                            <Badge className="ml-2 bg-purple-100 text-purple-700 border-0">From Invoice</Badge>
                          )}
                        </div>
                      )}
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={unreconcile} disabled={busy === 'unreconcile'} data-testid="invoice-unreconcile-btn">
                      {busy === 'unreconcile' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                      Revert to pending
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reconcile actions (only if pending) */}
            {!isReconciled && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-700">Reconcile this invoice</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setAction('match')}
                    className={`text-left p-3 rounded-lg border-2 transition ${action === 'match' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-emerald-300'}`}
                    data-testid="action-match"
                  >
                    <div className="flex items-center gap-1.5 mb-1"><Link2 className="w-4 h-4 text-emerald-600" /><span className="font-semibold text-sm">Match to order</span></div>
                    <p className="text-[11px] text-slate-600">Link to a Material Order you already raised in the app.</p>
                    <p className="text-[10px] text-emerald-700 mt-1">{suggestions.length} suggestion{suggestions.length === 1 ? '' : 's'}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAction('convert')}
                    className={`text-left p-3 rounded-lg border-2 transition ${action === 'convert' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 bg-white hover:border-purple-300'}`}
                    data-testid="action-convert"
                  >
                    <div className="flex items-center gap-1.5 mb-1"><FilePlus className="w-4 h-4 text-purple-600" /><span className="font-semibold text-sm">Create back-dated order</span></div>
                    <p className="text-[11px] text-slate-600">For purchases that never had a PO. The new order is labeled <b>From Invoice</b>.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAction('expense')}
                    className={`text-left p-3 rounded-lg border-2 transition ${action === 'expense' ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 bg-white hover:border-cyan-300'}`}
                    data-testid="action-expense"
                  >
                    <div className="flex items-center gap-1.5 mb-1"><Receipt className="w-4 h-4 text-cyan-600" /><span className="font-semibold text-sm">Log as project expense</span></div>
                    <p className="text-[11px] text-slate-600">No order — counts as one-off project spend (fuel, tools, etc.).</p>
                  </button>
                </div>

                {/* Action panels */}
                {action === 'match' && (
                  <Card className="border-emerald-200">
                    <CardContent className="p-3">
                      <div className="text-xs font-semibold text-slate-700 mb-2">Pick a Material Order to link</div>
                      {suggestions.length === 0 ? (
                        <div className="text-xs text-slate-500 py-2">No suggestions found. Try the other two options.</div>
                      ) : (
                        <div className="space-y-1.5 max-h-[260px] overflow-y-auto">
                          {suggestions.map(s => (
                            <button
                              type="button"
                              key={s.order_id}
                              onClick={() => setPickedOrderId(s.order_id)}
                              disabled={s.already_invoiced}
                              className={`w-full text-left p-2 rounded border text-xs transition ${
                                pickedOrderId === s.order_id ? 'border-emerald-500 bg-emerald-50' :
                                s.already_invoiced ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed' :
                                'border-slate-200 hover:border-emerald-300 bg-white'
                              }`}
                              data-testid={`suggestion-${s.order_id}`}
                            >
                              <div className="flex items-center justify-between flex-wrap gap-1">
                                <div className="font-medium text-slate-800">
                                  {s.project_name || 'Unassigned project'} · {s.order_date || '—'}
                                  {s.source === 'invoice_backdated' && <Badge className="ml-2 bg-purple-100 text-purple-700 border-0 text-[10px]">From Invoice</Badge>}
                                  {s.already_invoiced && <Badge className="ml-2 bg-amber-100 text-amber-800 border-0 text-[10px]">Already invoiced</Badge>}
                                </div>
                                <div className="text-emerald-700 font-semibold">{Math.round(s.score * 100)}% match</div>
                              </div>
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                Status: {s.status} · Total: {fmtMoney(s.total_cost)} · {s.reasons.join(', ')}
                              </div>
                              {s.notes && <div className="text-[10px] text-slate-400 mt-0.5 italic">{s.notes}</div>}
                            </button>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {action === 'convert' && (
                  <Card className="border-purple-200">
                    <CardContent className="p-3 space-y-2">
                      <div className="text-xs font-semibold text-slate-700">Create a back-dated Material Order</div>
                      <p className="text-[11px] text-slate-500">
                        Creates a Material Order with date <b>{data.invoice_date || 'today'}</b>, totals from this invoice, status <b>invoiced</b>,
                        and a purple <b>From Invoice</b> badge so it's clear this didn't come through the normal PO flow.
                      </p>
                      <div>
                        <Label className="text-xs">Assign to project *</Label>
                        <ProjectCombobox
                          projects={projects || []}
                          value={convProjectId}
                          onChange={setConvProjectId}
                          placeholder="Search project…"
                          testId="convert-project-combobox"
                        />
                        <NewProjectButton
                          defaultAddress={data.ship_to || ''}
                          onCreated={async (newId) => {
                            if (onProjectsChanged) await onProjectsChanged();
                            setConvProjectId(newId);
                          }}
                          testId="convert-new-project"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Optional note</Label>
                        <Textarea value={convNotes} onChange={(e) => setConvNotes(e.target.value)} className="text-xs" rows={2} placeholder="Anything to remember about this purchase…" />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {action === 'expense' && (
                  <Card className="border-cyan-200">
                    <CardContent className="p-3 space-y-2">
                      <div className="text-xs font-semibold text-slate-700">Log as standalone project expense</div>
                      <p className="text-[11px] text-slate-500">
                        Doesn't create a Material Order. Total <b>{fmtMoney(data.total, data.currency || 'CAD')}</b> rolls up against the chosen project.
                      </p>
                      <div>
                        <Label className="text-xs">Project *</Label>
                        <ProjectCombobox
                          projects={projects || []}
                          value={expProjectId}
                          onChange={setExpProjectId}
                          placeholder="Search project…"
                          testId="expense-project-combobox"
                        />
                        <NewProjectButton
                          defaultAddress={data.ship_to || ''}
                          onCreated={async (newId) => {
                            if (onProjectsChanged) await onProjectsChanged();
                            setExpProjectId(newId);
                          }}
                          testId="expense-new-project"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Optional note</Label>
                        <Textarea value={expNotes} onChange={(e) => setExpNotes(e.target.value)} className="text-xs" rows={2} />
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="review-dialog-cancel">Close</Button>
          {!isReconciled && action && (
            <Button type="button" onClick={submit} disabled={!!busy} data-testid="review-dialog-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              {action === 'match' && 'Match & save'}
              {action === 'convert' && 'Create back-dated order'}
              {action === 'expense' && 'Log expense'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------- Upload Card ----------------------------------
function UploadCard({ onUploaded }) {
  const [queue, setQueue] = useState([]); // [{id, file, status, progress}]
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const enqueue = (files) => {
    const items = Array.from(files).filter(f =>
      /\.(pdf|png|jpe?g)$/i.test(f.name)
    ).map(f => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      status: 'queued',
      progress: 0,
    }));
    if (items.length === 0) {
      toast.error('Only PDF / PNG / JPG files are supported');
      return;
    }
    setQueue(q => [...q, ...items]);
  };

  const uploadOne = useCallback(async (item) => {
    setQueue(q => q.map(x => x.id === item.id ? { ...x, status: 'uploading', progress: 30 } : x));
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', item.file);
    try {
      const res = await fetch(`${API_URL}/api/invoices/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Upload failed (${res.status})`);
      }
      setQueue(q => q.map(x => x.id === item.id ? { ...x, status: 'parsing', progress: 80 } : x));
      // Brief delay so the inbox shows the new item once parsing kicks off.
      setTimeout(() => {
        setQueue(q => q.map(x => x.id === item.id ? { ...x, status: 'done', progress: 100 } : x));
        if (onUploaded) onUploaded();
      }, 1200);
    } catch (e) {
      setQueue(q => q.map(x => x.id === item.id ? { ...x, status: 'error', error: e.message } : x));
    }
  }, [onUploaded]);

  // Drain the queue serially so we don't overload the LLM endpoint.
  useEffect(() => {
    const next = queue.find(q => q.status === 'queued');
    if (!next) return;
    uploadOne(next);
  }, [queue, uploadOne]);

  const clearDone = () => setQueue(q => q.filter(x => x.status !== 'done'));
  const removeItem = (id) => setQueue(q => q.filter(x => x.id !== id));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <UploadCloud className="w-4 h-4 text-blue-600" />
          Upload supplier invoices
          {queue.length > 0 && <Badge variant="secondary" className="ml-1">{queue.length} in queue</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); enqueue(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'}`}
          data-testid="invoice-drop-zone"
        >
          <UploadCloud className="w-8 h-8 mx-auto text-blue-500 mb-2" />
          <div className="text-sm font-semibold text-slate-700">Drop PDFs or photos here</div>
          <div className="text-xs text-slate-500">or click to pick — one file or many at once</div>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/*"
            multiple
            className="hidden"
            onChange={(e) => { enqueue(e.target.files); e.target.value = ''; }}
            data-testid="invoice-file-input"
          />
        </div>

        {queue.length > 0 && (
          <div className="mt-3 border rounded-lg divide-y" data-testid="invoice-upload-queue">
            {queue.map(q => (
              <div key={q.id} className="flex items-center gap-2 px-3 py-2 text-xs">
                <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{q.file.name}</div>
                  <div className="text-[10px] text-slate-500">
                    {q.status === 'queued' && 'Waiting…'}
                    {q.status === 'uploading' && 'Uploading…'}
                    {q.status === 'parsing' && 'Parsing with AI…'}
                    {q.status === 'done' && 'Sent to inbox'}
                    {q.status === 'error' && <span className="text-red-600">{q.error}</span>}
                  </div>
                </div>
                {q.status === 'uploading' || q.status === 'parsing' ? <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" /> :
                  q.status === 'done' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> :
                  q.status === 'error' ? <AlertTriangle className="w-3.5 h-3.5 text-red-500" /> :
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                <button type="button" onClick={() => removeItem(q.id)} className="text-slate-400 hover:text-red-500" aria-label="Remove">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {queue.some(x => x.status === 'done') && (
              <div className="px-3 py-2 flex justify-end">
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={clearDone}>Clear finished</Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------- Invoice Row ----------------------------------
function InvoiceRow({ inv, onOpen, onDelete }) {
  const status = inv.reconcile_status || (inv.parse_status === 'parsed' ? 'pending_review' : inv.parse_status);
  return (
    <div className="border rounded-lg p-3 bg-white hover:shadow-sm transition" data-testid={`invoice-row-${inv.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm truncate">{inv.supplier_name || inv.original_filename || 'Unparsed invoice'}</span>
            <StatusBadge status={status} />
            {inv.project_match_status === 'auto' && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">Auto-matched project</Badge>}
            {inv.project_match_status === 'needs_review' && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Needs project</Badge>}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
            <span>Invoice #{inv.invoice_number || '—'}</span>
            <span>{inv.invoice_date || ''}</span>
            {inv.project_name && <span className="text-slate-700">· {inv.project_name}</span>}
            <span className="font-medium text-slate-800">{fmtMoney(inv.total, inv.currency || 'CAD')}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onOpen(inv.id)} data-testid={`open-invoice-${inv.id}`}>
            <Eye className="w-3 h-3 mr-1" /> Review
          </Button>
          <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-500" onClick={() => onDelete(inv.id)} data-testid={`delete-invoice-${inv.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------- Main tab -------------------------------------
export function InvoiceManagement({ projects, onProjectsChanged }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [tab, setTab] = useState('pending');

  const fetchAll = useCallback(async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setInvoices(await res.json());
    } catch {
      // network error — keep prior list
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh every 6 seconds while any invoice is parsing.
  useEffect(() => {
    const anyParsing = invoices.some(i => i.parse_status === 'pending');
    if (!anyParsing) return;
    const t = setInterval(fetchAll, 6000);
    return () => clearInterval(t);
  }, [invoices, fetchAll]);

  const handleDelete = async (id) => {
    if (!confirm('Delete this invoice? This cannot be undone.')) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/api/invoices/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      toast.success('Deleted');
      fetchAll();
    } else {
      toast.error('Could not delete');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (i) => {
      if (!q) return true;
      return [i.supplier_name, i.invoice_number, i.project_name, i.original_filename]
        .filter(Boolean).some(v => v.toLowerCase().includes(q));
    };
    const pending = invoices.filter(i => match(i) && (i.parse_status === 'pending' || (i.parse_status === 'parsed' && (!i.reconcile_status || i.reconcile_status === 'pending_review')) || i.parse_status === 'failed'));
    const reconciled = invoices.filter(i => match(i) && ['reconciled_matched', 'reconciled_backdated', 'logged_as_expense'].includes(i.reconcile_status));
    return { pending, reconciled };
  }, [invoices, search]);

  return (
    <div className="space-y-4" data-testid="invoice-management-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Invoice Management</h2>
          <p className="text-xs text-slate-500">Upload supplier invoices · AI extracts line items · reconcile to a Material Order, back-date one, or log as expense.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="invoice-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <UploadCard onUploaded={fetchAll} />

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base">Invoice inbox</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search supplier / invoice #…" className="pl-7 h-8 text-xs" data-testid="invoice-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-3">
              <TabsTrigger value="pending" data-testid="invoice-tab-pending">
                Pending review <Badge variant="secondary" className="ml-2 h-5 text-[10px]">{filtered.pending.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="reconciled" data-testid="invoice-tab-reconciled">
                Reconciled <Badge variant="secondary" className="ml-2 h-5 text-[10px]">{filtered.reconciled.length}</Badge>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="pending" className="space-y-2">
              {filtered.pending.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">Nothing waiting. Drop an invoice above to get started.</div>
              ) : filtered.pending.map(inv => (
                <InvoiceRow key={inv.id} inv={inv} onOpen={(id) => setActiveId(id)} onDelete={handleDelete} />
              ))}
            </TabsContent>
            <TabsContent value="reconciled" className="space-y-2">
              {filtered.reconciled.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">No reconciled invoices yet.</div>
              ) : filtered.reconciled.map(inv => (
                <InvoiceRow key={inv.id} inv={inv} onOpen={(id) => setActiveId(id)} onDelete={handleDelete} />
              ))}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <ReviewDialog
        open={!!activeId}
        onOpenChange={(v) => { if (!v) setActiveId(null); }}
        invoiceId={activeId}
        projects={projects}
        onReconciled={fetchAll}
        onProjectsChanged={onProjectsChanged}
      />
    </div>
  );
}

export default InvoiceManagement;
